import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { rateCardFormValue } from 'v5/core/entity-values/codecs/rate.codec';
import type { RateFormValue } from 'v5/core/entity-values/values.types';
import type { FieldComponentProps } from '../fieldComponent.types';
import { describeRate } from 'v5/core/rates/describeRate';
import {
  RateCardBadge,
  RateCardUnavailableBadge,
  RateMoney
} from './RateSummary';
import { useRateCopy } from './useRateCopy';

/**
 * A rate row's header while the list is edited: the name as typed and the
 * amount under it, so a row says which rate it is while scrolled into its
 * fields. A rate-card row is named from the card, with its badge.
 */
export function RateRowHeading({ spec, path }: FieldComponentProps<'rate'>) {
  const { t } = useTranslation('core');
  const copy = useRateCopy();
  const value = useWatch<Record<string, RateFormValue>>({ name: path });
  const rateId = value.rate_id ?? null;
  const card =
    rateId === null
      ? null
      : (spec.rateCards.find(option => option.id === rateId) ?? null);

  if (rateId !== null && card === null) {
    return (
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate font-semibold text-fg-default">
          {t('rateEditor.rateCardFallbackName', { id: rateId })}
        </span>
        <span className="shrink-0">
          <RateCardUnavailableBadge />
        </span>
      </span>
    );
  }

  const rate = describeRate(
    card === null ? value : rateCardFormValue(card),
    copy
  );
  return (
    <span className="flex min-w-0 flex-col leading-tight">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate font-semibold text-fg-default">
          {rate.name}
        </span>
        {card !== null && (
          <span className="shrink-0">
            <RateCardBadge />
          </span>
        )}
      </span>
      <span className="truncate text-sm font-normal tabular-nums text-fg-default">
        <RateMoney money={rate.money} />
      </span>
    </span>
  );
}
